import { useState } from 'react';
import {
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignHorizontalSpaceAround,
  AlignVerticalSpaceAround,
} from 'lucide-react';
import type { AlignAnchor, AlignTarget, DistributeAxis } from '../lib/layer-align';

interface AlignPanelProps {
  /** Number of currently-selected layers. Drives the
   *  enable/disable state of the buttons: alignment requires 2+,
   *  distribution requires 3+. */
  selectedCount: number;
  /** Fired when an align button is clicked. EditorPage gathers
   *  the selected layers' bboxes, computes deltas via
   *  `computeAlignDeltas`, and applies via `applyLayerOffsets`. */
  onAlign: (anchor: AlignAnchor, target: AlignTarget) => void;
  /** Fired when a distribute button is clicked. EditorPage routes
   *  to `computeDistributeDeltas` + `applyLayerOffsets`. */
  onDistribute: (axis: DistributeAxis) => void;
}

/** Align & Distribute compact toolbar. Visible when 2+ layers
 *  are selected. Six align icons (left / center-H / right / top
 *  / center-V / bottom) + Align-to selector (Selection / Canvas)
 *  + Distribute Horizontal / Vertical (3+ layers).
 *
 *  Tier 2 Group D. Pure presentational; EditorPage owns the
 *  bbox-gather + delta-compute + Fabric-apply pipeline so this
 *  component stays unit-testable in isolation. */
export function AlignPanel({ selectedCount, onAlign, onDistribute }: AlignPanelProps) {
  const [target, setTarget] = useState<AlignTarget>('selection');
  const canDistribute = selectedCount >= 3;

  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-muted/40 border-b border-border">
      {/* Align-to selector */}
      <select
        value={target}
        onChange={(e) => setTarget(e.target.value as AlignTarget)}
        className="text-xs bg-background border border-border rounded px-1.5 py-0.5"
        aria-label="Align relative to"
      >
        <option value="selection">Selection</option>
        <option value="canvas">Canvas</option>
      </select>
      <span className="w-px h-5 bg-border mx-1" />
      <AlignButton
        icon={AlignStartVertical}
        title="Align left edges"
        onClick={() => onAlign('left', target)}
      />
      <AlignButton
        icon={AlignCenterVertical}
        title="Align horizontal centers"
        onClick={() => onAlign('center-x', target)}
      />
      <AlignButton
        icon={AlignEndVertical}
        title="Align right edges"
        onClick={() => onAlign('right', target)}
      />
      <span className="w-px h-5 bg-border mx-1" />
      <AlignButton
        icon={AlignStartHorizontal}
        title="Align top edges"
        onClick={() => onAlign('top', target)}
      />
      <AlignButton
        icon={AlignCenterHorizontal}
        title="Align vertical centers"
        onClick={() => onAlign('center-y', target)}
      />
      <AlignButton
        icon={AlignEndHorizontal}
        title="Align bottom edges"
        onClick={() => onAlign('bottom', target)}
      />
      <span className="w-px h-5 bg-border mx-1" />
      <AlignButton
        icon={AlignHorizontalSpaceAround}
        title="Distribute horizontally (needs 3+)"
        disabled={!canDistribute}
        onClick={() => onDistribute('horizontal')}
      />
      <AlignButton
        icon={AlignVerticalSpaceAround}
        title="Distribute vertically (needs 3+)"
        disabled={!canDistribute}
        onClick={() => onDistribute('vertical')}
      />
    </div>
  );
}

function AlignButton({
  icon: Icon,
  title,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
